import { useLocalStorage } from "@vueuse/core";
import { computed } from "vue";

export enum LogLevel {
    TRACE = 0,
    DEBUG = 1,
    INFO = 2,
    WARN = 3,
    ERROR = 4,
}

export type LogFn = typeof console.log;

export type Logger = {
    trace: LogFn;
    debug: LogFn;
    info: LogFn;
    warn: LogFn;
    error: LogFn;
}

export const LogLevelColor: { [key in LogLevel]: string } = {
    [LogLevel.TRACE]: "",
    [LogLevel.DEBUG]: "color: #b294bb",
    [LogLevel.INFO]: "color: #b5bd68",
    [LogLevel.WARN]: "color: #f0c674",
    [LogLevel.ERROR]: "color: #cc6666"
}
export const LogLevelPrefix: { [key in LogLevel]: string } = {
    [LogLevel.TRACE]: "[TRACE]",
    [LogLevel.DEBUG]: "[DEBUG]",
    [LogLevel.INFO]: "[INFO]",
    [LogLevel.WARN]: "[WARN]",
    [LogLevel.ERROR]: "[ERROR]"
}

export const LogLevelFn: { [key in LogLevel]: LogFn } = {
    [LogLevel.TRACE]: console.debug,
    [LogLevel.DEBUG]: console.debug,
    [LogLevel.INFO]: console.log,
    [LogLevel.WARN]: console.warn,
    [LogLevel.ERROR]: console.error,
}

const _savedLevel = useLocalStorage('_JB_LOG_LEVEL', null);
const _level = computed(() => {
    return _savedLevel.value ?? (import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR);
});

export function useLogger(): Logger {
    const createLogFn: (level: LogLevel) => LogFn = (level) => {
        return (...args) => {
            if (level >= _level.value) {
                LogLevelFn[level].apply(console, ['%c' + LogLevelPrefix[level], LogLevelColor[level], ...args]);
            }
        }
    };
    return {
        trace: createLogFn(LogLevel.TRACE),
        debug: createLogFn(LogLevel.DEBUG),
        info: createLogFn(LogLevel.INFO),
        warn: createLogFn(LogLevel.WARN),
        error: createLogFn(LogLevel.ERROR),
    }
}
